<script setup lang="ts">
import { computed } from 'vue'
import type { CSSProperties } from 'vue'

import { useReportCellFormatter } from '../../../composables/useReportCellFormatter'
import type { ReportColumn } from '../../../models/column.types'
import type { ReportRow } from '../../../models/report.types'
import { performanceScoreColor } from '../../../service/structuredCells'

import { useI18n } from '@/shared/i18n'

const props = defineProps<{
  row: ReportRow
  column: ReportColumn
}>()

const { t } = useI18n()
const { formatCell } = useReportCellFormatter()
const rawValue = computed(() => Number(props.row[props.column.field]))
// v3 dashes an exact-zero score rather than painting a red 0 (`OrphexTd.vue:226`) — a genuine
// low score still renders (e.g. 2), only the "no score yet" sentinel is hidden.
const hasScore = computed(() => Number.isFinite(rawValue.value) && rawValue.value !== 0)
const displayValue = computed(() => formatCell(props.row[props.column.field], props.column))
const style = computed<CSSProperties>(() => {
  const color = performanceScoreColor(rawValue.value)
  return { color, backgroundColor: `${color}30` }
})
</script>

<template>
  <span class="block w-full text-center">
    <span
      v-if="hasScore"
      class="report-performance-score-cell rounded-sm px-xs py-2xs text-xs font-semibold"
      :style="style"
    >
      {{ displayValue }}
    </span>
    <span v-else class="text-muted text-xs">{{ t('reports.table.cellUnavailable') }}</span>
  </span>
</template>
